import type { DraftRule, SpecStrictness, SpecVersion } from "@shared/auspost/model.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { Card, FIELD, PageHeader, PRIMARY_BTN } from "../components/account-ui.tsx";
import { useToast } from "../components/legacy/ui.tsx";
import { type AppConfig, api } from "../lib/api.ts";
import { isStaffRole } from "../lib/legacy.ts";

export const Route = createFileRoute("/app/$customerId/auspost-specs")({
  component: AusPostSpecs,
  beforeLoad: async ({ context, params }) => {
    const config = await context.queryClient.ensureQueryData({
      queryKey: ["config"],
      queryFn: () => api.get<AppConfig>("/config"),
    });
    if (!config.user || !isStaffRole(config.user.role)) {
      throw redirect({
        to: "/app/$customerId/mycampaigns",
        params: { customerId: params.customerId },
      });
    }
  },
});

interface SpecLine extends DraftRule {
  statement: string;
  why: string;
  sourceTitle: string;
  auspostDocId: string;
  sourceDate: string;
  strictness: SpecStrictness;
}

interface SpecPayload {
  current: { version: SpecVersion; lines: SpecLine[] };
  versions: SpecVersion[];
  documents: { id: string; title: string; versionLabel: string }[];
}

interface DiffRow {
  key: string;
  before: unknown;
  after: unknown;
}

function lineId(line: {
  service: string | null;
  key: string;
  presentation: string | null;
  mailType: string | null;
  mailSpeed: string | null;
  sizeCategory: string | null;
}): string {
  return [
    line.service,
    line.key,
    line.presentation,
    line.mailType,
    line.mailSpeed,
    line.sizeCategory,
  ].join("|");
}

function when(seconds: number): string {
  return new Date(seconds * 1000).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function AusPostSpecs() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const data = useQuery({
    queryKey: ["auspost-spec-admin"],
    queryFn: () => api.get<SpecPayload>("/admin/auspost-spec"),
  });
  const [changelog, setChangelog] = useState("");
  const [edits, setEdits] = useState<Record<string, { valueText: string; clause: string }>>({});
  const [fromId, setFromId] = useState("");
  const [toId, setToId] = useState("");
  const [diff, setDiff] = useState<DiffRow[] | null>(null);
  const [viewId, setViewId] = useState<string | null>(null);

  const viewed = useQuery({
    queryKey: ["auspost-spec-version", viewId],
    enabled: Boolean(viewId),
    queryFn: () =>
      api.get<{
        version: SpecVersion;
        lines: {
          key: string;
          statement: string;
          value: unknown;
          sourceClause: string;
          sourceDate: string;
        }[];
        rules: { id: string; service: string; key: string }[];
      }>(`/admin/auspost-spec/versions/${viewId}`),
  });

  const publish = useMutation({
    mutationFn: () => {
      const lines = data.data?.current.lines ?? [];
      const rules = lines.map((line) => {
        const edit = edits[lineId(line)];
        const valueText = edit?.valueText ?? JSON.stringify(line.value);
        return {
          ...line,
          value: JSON.parse(valueText) as unknown,
          sourceClause: edit?.clause ?? line.sourceClause,
        };
      });
      return api.post<{ id: string }>("/admin/auspost-spec/versions", {
        changelog,
        rules,
      });
    },
    onSuccess: () => {
      setChangelog("");
      setEdits({});
      queryClient.invalidateQueries({ queryKey: ["auspost-spec-admin"] });
      queryClient.invalidateQueries({ queryKey: ["auspost-spec"] });
      showToast("Spec version published", "success");
    },
    onError: (err: Error) => showToast(err.message, "error"),
  });

  const rollback = useMutation({
    mutationFn: (versionId: string) =>
      api.post<{ id: string }>("/admin/auspost-spec/rollback", {
        versionId,
        changelog: `Restored ${versionId}`,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auspost-spec-admin"] });
      queryClient.invalidateQueries({ queryKey: ["auspost-spec"] });
      showToast("Spec restored as a new version", "success");
    },
    onError: (err: Error) => showToast(err.message, "error"),
  });

  const compare = useMutation({
    mutationFn: () =>
      api.get<{ diff: DiffRow[] }>(`/admin/auspost-spec/diff?from=${fromId}&to=${toId}`),
    onSuccess: (body) => setDiff(body.diff),
    onError: (err: Error) => showToast(err.message, "error"),
  });

  const current = data.data?.current;
  const versions = data.data?.versions ?? [];

  return (
    <div>
      <PageHeader
        title="AusPost specs"
        subtitle={
          current
            ? `Current version from ${when(current.version.effectiveFrom)}. ${current.version.changelog}`
            : "Loading the current letter spec."
        }
      />
      <Card title="Current rules">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-gray-100 text-xs font-semibold uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2">Service</th>
                <th className="px-3 py-2">Rule</th>
                <th className="px-3 py-2">Value</th>
                <th className="px-3 py-2">Source</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(current?.lines ?? []).map((line) => (
                <tr key={lineId(line)}>
                  <td className="px-3 py-3 align-top text-xs text-gray-600">
                    {line.service ?? "any"}
                  </td>
                  <td className="px-3 py-3 align-top">
                    <p className="font-medium text-gray-900">{line.statement}</p>
                    <p className="mt-1 text-xs text-gray-500">{line.key}</p>
                  </td>
                  <td className="px-3 py-3 align-top font-mono text-xs">
                    {JSON.stringify(line.value)} {line.unit}
                  </td>
                  <td className="px-3 py-3 align-top text-xs text-gray-600">
                    {line.sourceTitle} ({line.auspostDocId}), {line.sourceClause}, {line.sourceDate}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="New version" className="mt-6">
        <p className="mb-4 text-sm text-gray-600">
          Publishing inserts a new version and closes the current one. Stored values are not
          overwritten.
        </p>
        <label className="mb-4 block text-sm">
          Changelog
          <input
            className={`${FIELD} mt-1`}
            value={changelog}
            onChange={(event) => setChangelog(event.target.value)}
          />
        </label>
        <div className="space-y-4">
          {(current?.lines ?? []).map((line) => {
            const id = lineId(line);
            const edit = edits[id] ?? {
              valueText: JSON.stringify(line.value),
              clause: line.sourceClause,
            };
            return (
              <div key={id} className="grid gap-2 border-t border-gray-100 pt-3 md:grid-cols-2">
                <p className="text-sm text-gray-800 md:col-span-2">
                  {line.service ?? "any"} · {line.key}
                </p>
                <label className="block text-xs text-gray-500">
                  Value
                  <textarea
                    className={`${FIELD} mt-1 font-mono`}
                    rows={2}
                    value={edit.valueText}
                    onChange={(event) =>
                      setEdits((prev) => ({
                        ...prev,
                        [id]: { ...edit, valueText: event.target.value },
                      }))
                    }
                  />
                </label>
                <label className="block text-xs text-gray-500">
                  Clause
                  <input
                    className={`${FIELD} mt-1`}
                    value={edit.clause}
                    onChange={(event) =>
                      setEdits((prev) => ({
                        ...prev,
                        [id]: { ...edit, clause: event.target.value },
                      }))
                    }
                  />
                </label>
              </div>
            );
          })}
        </div>
        <button
          type="button"
          className={`${PRIMARY_BTN} mt-4`}
          disabled={publish.isPending || changelog.trim() === ""}
          onClick={() => publish.mutate()}
        >
          Publish version
        </button>
      </Card>

      <Card title="Compare versions" className="mt-6">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm">
            From
            <select
              className={`${FIELD} mt-1`}
              value={fromId}
              onChange={(event) => setFromId(event.target.value)}
            >
              <option value="">Choose</option>
              {versions.map((version) => (
                <option key={version.id} value={version.id}>
                  {when(version.effectiveFrom)} {version.changelog}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            To
            <select
              className={`${FIELD} mt-1`}
              value={toId}
              onChange={(event) => setToId(event.target.value)}
            >
              <option value="">Choose</option>
              {versions.map((version) => (
                <option key={version.id} value={version.id}>
                  {when(version.effectiveFrom)} {version.changelog}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className={PRIMARY_BTN}
            disabled={!fromId || !toId || compare.isPending}
            onClick={() => compare.mutate()}
          >
            Show diff
          </button>
        </div>
        {diff && (
          <ul className="mt-4 space-y-2 text-sm">
            {diff.length === 0 ? <li>No value changes.</li> : null}
            {diff.map((row) => (
              <li key={row.key}>
                <span className="font-medium">{row.key}</span>: {JSON.stringify(row.before)} →{" "}
                {JSON.stringify(row.after)}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title="Earlier versions" className="mt-6">
        <ul className="divide-y divide-gray-100 text-sm">
          {versions.map((version) => (
            <li key={version.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
              <div>
                <p className="font-medium text-gray-900">{version.changelog}</p>
                <p className="text-xs text-gray-500">
                  {when(version.effectiveFrom)}
                  {version.effectiveTo ? ` until ${when(version.effectiveTo)}` : " (current)"}
                </p>
              </div>
              <div className="flex gap-2">
                <button type="button" className={PRIMARY_BTN} onClick={() => setViewId(version.id)}>
                  View
                </button>
                {version.effectiveTo !== null ? (
                  <button
                    type="button"
                    className={PRIMARY_BTN}
                    disabled={rollback.isPending}
                    onClick={() => rollback.mutate(version.id)}
                  >
                    Restore
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
        {viewed.data && (
          <div className="mt-4 border-t border-gray-100 pt-4">
            <p className="mb-2 text-sm font-medium">{viewed.data.version.changelog}</p>
            <ul className="space-y-1 text-xs text-gray-700">
              {viewed.data.lines.map((line, index) => (
                <li key={viewed.data?.rules[index]?.id ?? `${line.key}-${index}`}>
                  {viewed.data?.rules[index]?.service} · {line.key}: {JSON.stringify(line.value)} —{" "}
                  {line.sourceClause}, {line.sourceDate}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card>
    </div>
  );
}
